
import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTenant } from '../Context/TenantContext';
import { UserContext } from '../BasicComponents/UserContext';
import { hasTsgAiAccess, TSG_AI_URL } from '../utils/tsgAiAccess';
import {
  HomeIcon,
  UserIcon,
  BookOpenIcon,
  RocketLaunchIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  CodeBracketIcon,
  ChevronDownIcon,
  AcademicCapIcon,
  SparklesIcon,
  BriefcaseIcon,
  MagnifyingGlassIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import { Github, Award } from 'lucide-react';

/**
 * TenantSidebar
 *
 * Premium enterprise-grade navigation sidebar for tenant students.
 * Features Royal Purple design system, search, and refined interactions.
 */
const TenantSidebar = ({ variant = 'desktop', onRequestClose, expanded: expandedProp, onExpandedChange }) => {
  const [expandedInternal, setExpandedInternal] = useState(true);
  const isControlled = expandedProp !== undefined;
  const expanded     = isControlled ? expandedProp : expandedInternal;
  
  const setExpanded  = (valueOrUpdater) => {
    const next = typeof valueOrUpdater === 'function' ? valueOrUpdater(expanded) : valueOrUpdater;
    if (isControlled) {
      onExpandedChange?.(next);
    } else {
      setExpandedInternal(next);
    }
  };

  const [openDropdown, setOpenDropdown] = useState('');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [hasTsgAi, setHasTsgAi] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const { tenant } = useTenant();
  const [logoError, setLogoError] = useState(false);
  const { setUser } = useContext(UserContext);

  const isMobile = variant === 'mobile';

  useEffect(() => {
    const userId = localStorage.getItem('user_id');
    if (userId) {
      checkTsgAiAccess(userId);
      const id = setInterval(() => checkTsgAiAccess(userId), 30000);
      return () => clearInterval(id);
    }
  }, []);

  const checkTsgAiAccess = async (userId) => {
    try {
      const access = await hasTsgAiAccess(userId);
      setHasTsgAi(access.hasAccess);
    } catch {
      setHasTsgAi(false);
    }
  };

  useEffect(() => {
    if (location.pathname.startsWith('/bootcamp/')) {
      setOpenDropdown('BootCamp');
    }
  }, [location.pathname]);

  const toggleDropdown = (label) =>
    setOpenDropdown((prev) => (prev === label ? '' : label));

  const handleLogout = () => {
    localStorage.clear();
    setUser(null);
    setShowLogoutModal(false);
    navigate('/student/login');
  };

  const navItems = [
    { label: 'Dashboard', icon: <HomeIcon className="h-5 w-5" />, path: '/dashboard' },
    { 
      label: 'My Profile', 
      icon: <UserIcon className="h-5 w-5" />, 
      subItems: [
        { label: 'View Profile', path: '/profile' },
        { label: 'Edit Profile', path: '/edit-profile' },
      ],
    },
    {
      label: 'Tech Explorer',
      icon: <BookOpenIcon className="h-5 w-5" />,
      subItems: [
        { label: 'Technology MCQs', path: '/techgrid' },
        { label: 'Tech Documents', path: '/techmcq' },
      ],
    },
    {
      label: 'Launchpad',
      icon: <RocketLaunchIcon className="h-5 w-5" />,
      subItems: [
        { label: 'Launchpad MCQs', path: '/launchpadpage' },
        { label: 'Launchpad Docs', path: '/topicexplorer' },
      ],
    },
    { label: 'Compiler', icon: <CodeBracketIcon className="h-5 w-5" />, path: '/compiler' },
    {
      label: 'Bootcamp',
      icon: <AcademicCapIcon className="h-5 w-5" />,
      subItems: [
        { label: 'Enroll Now', path: '/bootcamp/enrollment' },
        { label: 'My Bootcamps', path: '/bootcamp/my-bootcamps' },
      ],
    },
    { label: 'My Certifications', icon: <Award size={20} />, path: '/student/certifications' },
    {
      label: 'Elite Technology Master',
      icon: <TrophyIcon className="h-5 w-5" />,
      isHighlighted: true,
      onClick: () => navigate('/elite-mastery'),
    },
    {
      label: 'TSG Notes',
      icon: <SparklesIcon className="h-5 w-5" />,
      subItems: [
        { label: 'AI Tutor (@TSG)', path: '/student/tsg' },
        { label: 'My Notes', path: '/student/tsg' },
        { label: 'TSGNotes', path: '/student/tsg' },
      ],
    },
    ...(hasTsgAi ? [{
      label: 'TSG AI',
      icon: <SparklesIcon className="h-5 w-5" />,
      isHighlighted: true,
      onClick: async () => {
        const userId = localStorage.getItem('user_id') || JSON.parse(localStorage.getItem('user') || '{}')?.userId;
        if (userId) {
          try {
            const access = await hasTsgAiAccess(userId);
            if (access.hasAccess) window.open(TSG_AI_URL, '_blank', 'noopener,noreferrer');
            else navigate('/skillgenpayments');
          } catch { navigate('/skillgenpayments'); }
        } else { navigate('/skillgenpayments'); }
      },
    }] : []),
    { label: 'Mock Interview', icon: <BriefcaseIcon className="h-5 w-5" />, path: '/student/mockinterviewhome' },
    { label: 'Support', icon: <ChatBubbleLeftRightIcon className="h-5 w-5" />, path: '/support-student' },
  ];

  const filteredItems = searchQuery 
    ? navItems.filter(item => 
        item.label.toLowerCase().includes(searchQuery.toLowerCase()) || 
        item.subItems?.some(sub => sub.label.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : navItems;

  const isActive = (item) => {
    if (item.path) {
      if (item.path === '/student/dashboard') return location.pathname === item.path;
      return location.pathname.startsWith(item.path);
    }
    if (item.label === 'Elite Technology Master') {
      return location.pathname.startsWith('/elite-mastery');
    }
    return false;
  };

  const isGroupActive = (subItems) => subItems?.some((i) => isActive(i)) ?? false;

  return (
    <>
      <div
        className={`
          ${isMobile ? 'h-full w-full' : `fixed top-0 left-0 h-screen z-20 transition-all duration-300 ${expanded ? 'w-64' : 'w-[72px]'}`}
          flex flex-col border-r border-white/10 shadow-sm
        `}
        style={{ backgroundColor: '#843FEF', color: '#FFFFFF' }}
      >
        {/* Logo Section */}
        <div className={`flex items-center gap-3 h-[72px] px-5 border-b border-white/10 ${expanded ? '' : 'justify-start px-[22px]'}`}>
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shadow-lg shadow-purple-900/20">
             {tenant?.logoUrl && !logoError ? (
                <img src={tenant.logoUrl} alt="Logo" className="w-7 h-7 object-contain" onError={() => setLogoError(true)} />
             ) : (
                <SparklesIcon className="h-6 w-6 text-white" />
             )}
          </div>
          {expanded && (
            <div className="flex flex-col min-w-0 animate-fade-in">
              <span className="text-sm font-black text-white tracking-tight truncate">The Skill Genie</span>
              <span className="text-[10px] font-bold text-white uppercase tracking-widest truncate">AI Powered Learning</span>
            </div>
          )}
        </div>

        {/* Search Bar */}
        {expanded && (
          <div className="px-4 py-4 animate-fade-in">
            <div className="relative group">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white group-focus-within:text-white transition-colors" />
              <input 
                type="text" 
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-white/10 border border-transparent rounded-xl text-xs font-medium text-white outline-none focus:border-white/30 focus:ring-4 focus:ring-white/5 transition-all placeholder:text-white"
              />
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1 custom-scrollbar">
          {filteredItems.map((item) => {
            const active = item.path ? isActive(item.path) : isGroupActive(item.subItems);
            const dropOpen = openDropdown === item.label;

            return (
              <div key={item.label} className="relative group/nav">
                <button
                  onClick={() => {
                    if (item.onClick) {
                      item.onClick();
                      if (!item.subItems?.length) {
                        if (!isMobile) setExpanded(false);
                        else onRequestClose?.();
                      }
                    }
                    else if (item.subItems?.length) {
                      if (expanded) toggleDropdown(item.label);
                      else { setExpanded(true); setOpenDropdown(item.label); }
                    } else if (item.path) {
                      navigate(item.path);
                      if (!isMobile) setExpanded(false);
                      else onRequestClose?.();
                    }
                  }}
                  className={`
                    flex items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all duration-200
                    ${item.label === 'Elite Mastery'
                      ? 'bg-gradient-to-r from-amber-400 to-orange-400 !text-white !font-black shadow-lg shadow-amber-300/40 hover:shadow-amber-300/60'
                      : active
                      ? 'bg-white/20 text-white font-bold shadow-sm'
                      : 'text-white hover:bg-white/10 hover:text-white'
                    }
                    ${expanded ? '' : 'justify-start pl-[18px]'}
                    ${item.isRoyalPurple ? '!bg-white !text-[#843FEF] border border-white shadow-lg shadow-purple-900/20' : ''}
                    custom-tooltip-trigger
                  `}
                  data-tooltip={item.label}
                  title={item.label}
                >
                  <span className={`flex-shrink-0 transition-transform duration-200 ${active ? 'scale-110' : 'group-hover/nav:scale-110'}`}>
                    {item.icon}
                  </span>
                  
                  {expanded ? (
                    <>
                      <span className="flex-1 text-xs truncate text-left">{item.label}</span>
                      {item.subItems?.length > 0 && (
                        <ChevronDownIcon className={`h-3 w-3 transition-transform duration-300 ${dropOpen ? 'rotate-180' : ''}`} />
                      )}
                    </>
                  ) : (
                    <div className="absolute left-full ml-4 px-2 py-1 bg-[#111827] text-white text-[10px] font-bold rounded opacity-0 invisible group-hover/nav:opacity-100 group-hover/nav:visible transition-all whitespace-nowrap z-50">
                      {item.label}
                    </div>
                  )}
                </button>

                {expanded && dropOpen && item.subItems?.length > 0 && (
                  <div className="mt-1 ml-4 pl-4 border-l border-white/20 space-y-1 animate-slide-up">
                    {item.subItems.map((sub) => {
                      const subActive = isActive(sub.path);
                      return (
                        <button
                          key={sub.label}
                          onClick={() => {
                            navigate(sub.path);
                            if (!isMobile) setExpanded(false);
                            else onRequestClose?.();
                          }}
                          className={`
                            w-full text-left py-1.5 px-3 rounded-lg text-[11px] transition-all
                            ${subActive ? 'text-white font-bold bg-white/10' : 'text-white hover:text-white hover:bg-white/5'}
                            custom-tooltip-trigger
                          `}
                          data-tooltip={sub.label}
                        >
                          {sub.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Desktop Collapse Toggle */}
        {!isMobile && (
          <div className="px-3 py-2 border-t border-white/10">
            <button 
              onClick={() => setExpanded(!expanded)}
              className="w-full flex items-center justify-center p-2 rounded-xl text-white hover:text-white hover:bg-white/10 transition-all"
            >
              {expanded ? <Bars3Icon className="h-5 w-5 rotate-180" /> : <Bars3Icon className="h-5 w-5" />}
            </button>
          </div>
        )}

        {/* User / Sign Out */}
        <div className="p-3 border-t border-white/10">
          <button
            onClick={() => setShowLogoutModal(true)}
            className={`
              flex items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all
              text-white hover:bg-red-500/20 font-bold
              ${expanded ? '' : 'justify-start pl-[18px]'}
              custom-tooltip-trigger
            `}
            data-tooltip="Sign Out"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5 flex-shrink-0" />
            {expanded && <span className="text-xs text-left">Sign Out</span>}
          </button>
        </div>
      </div>

      {/* Logout Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 flex items-center justify-center z-[100] p-4 bg-slate-900/40 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl border border-slate-100 animate-scale-in">
            <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <ArrowRightOnRectangleIcon className="h-7 w-7 text-red-500" />
            </div>
            <h2 className="text-xl font-black text-[#111827] mb-2 text-center">Ready to leave?</h2>
            <p className="text-sm text-[#6B7280] mb-8 text-center leading-relaxed">Your progress is saved. See you back soon!</p>
            <div className="flex gap-3">
              <button onClick={() => setShowLogoutModal(false)} className="flex-1 py-3 rounded-xl font-bold text-sm text-[#6B7280] bg-[#F8FAFC] hover:bg-[#F1F5F9] transition-colors">Cancel</button>
              <button onClick={handleLogout} className="flex-1 py-3 rounded-xl font-bold text-sm text-white bg-red-500 hover:bg-red-600 shadow-lg shadow-red-200 transition-all active:scale-95">Sign Out</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TenantSidebar;
